/**
 * Offene Vorschuss-Meldungen der Firma (Phase I, Kapitel 16.3 B) — nur für
 * den Chef sichtbar (diese Seite, TeamOverviewPage, ist bereits
 * Chef-exklusiv, siehe ProtectedRoute requireManage). Zeigt auch das
 * hochgeladene Foto, falls vorhanden (Kapitel 16.5: nur der Chef sieht
 * fremde Umschlag-Fotos).
 */

import { useState } from 'react';
import { usePendingAdvances, useDecidePayout } from '../data/payouts';
import { useAuth } from '../store/auth';
import { formatMoney, formatDateTime } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';

export function AdvanceDecisionsCard() {
  const { data: advances } = usePendingAdvances();
  const decide = useDecidePayout();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState<string | null>(null);
  // Gesamtprüfung 11.09.2026: Admins sehen Beträge nur als 0,00 € und
  // entscheiden nicht über Vorschüsse (Server: 403) — das macht der Chef.
  const me = useAuth((s) => s.user);
  const isAdmin = me?.role === 'admin' || me?.role === 'superadmin';

  if (isAdmin || !advances || advances.length === 0) return null;

  async function onDecide(id: string, decision: 'confirmed' | 'rejected') {
    setBusyId(id);
    try {
      await decide.mutateAsync({ id, decision });
      hapticSuccess();
    } catch {
      hapticError();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="card p-4">
      <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-3">
        Offene Vorschüsse ({advances.length})
      </h3>
      <ul className="space-y-2">
        {advances.map((a) => (
          <li
            key={a.id}
            className="bg-slate-900/40 rounded p-3 flex items-center gap-3 border-l-4 border-amber-500"
          >
            {a.photoData && a.photoData !== '__hidden__' && (
              <button
                type="button"
                onClick={() => setZoomed(a.photoData)}
                className="shrink-0"
                aria-label="Foto vergrößern"
              >
                <img
                  src={a.photoData}
                  alt="Beleg"
                  className="w-12 h-12 object-cover rounded border border-border/60"
                />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">
                {a.userName} · <span className="text-amber-400">{formatMoney(a.amount)}</span>
              </p>
              <p className="text-xs text-slate-400">
                {formatDateTime(a.createdAt)}
                {a.note ? ` · „${a.note}"` : ''}
              </p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={() => onDecide(a.id, 'confirmed')}
                disabled={busyId === a.id}
                className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-2.5 py-1.5"
              >
                Bestätigen
              </button>
              <button
                onClick={() => onDecide(a.id, 'rejected')}
                disabled={busyId === a.id}
                className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2.5 py-1.5"
              >
                Ablehnen
              </button>
            </div>
          </li>
        ))}
      </ul>

      {zoomed && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[70]"
          onClick={() => setZoomed(null)}
        >
          <img src={zoomed} alt="Beleg (groß)" className="max-w-full max-h-full rounded" />
        </div>
      )}
    </section>
  );
}
